import { getAppEnv } from "./getBrowserEnv";
import { getEnvironmentValue } from "../config";
import { getUrlParams } from "./urlParams";
import type { LoginOptions } from "../types/api.d";
import { loginInMtApp } from "./bridge/login";
import { invokeMiniProgramLogin } from "./miniprogram-login";
import { getUserInfo } from "@/api/login";
import type { UserInfo } from "@/context/PageStateContext";
import { isServer } from "./constant";
import { reportCustomMetric } from "./owl";

type GetUserInfoErrMsg = "success" | "tokenEmpty" | "tokenExpired" | "getFail";
// 缓存当前的用户信息
let cachedUserInfo: UserInfo | null = isServer ? null : getUserInfoFromUrl();

/**
 * 上报获取用户信息成功率
 * @param isSuccess 是否成功
 * @param errMsg 错误信息，默认 success
 */
const reportGetUserInfoSuccessRate = (
  isSuccess: boolean,
  errMsg: GetUserInfoErrMsg = "success",
) => {
  reportCustomMetric({
    key: "getUserInfo",
    value: isSuccess ? 1 : 0,
    tags: {
      errMsg,
    },
  });
};

/**
 * 跳转到i版登录页（兜底方案）fetchUserInfoClient
 *
 * 当美团APP环境判断为KNB环境但桥未就绪时，使用此方法作为兜底方案
 * 文档来源：I版登录页文档（https://km.sankuai.com/collabpage/2726928774）
 *
 * @param backUrl 登录成功后的回调URL，默认使用当前页面URL
 */
function redirectToILogin(backUrl?: string): void {
  if (isServer) {
    return;
  }

  // 获取登录页URL
  const loginUrl = getEnvironmentValue(
    {
      development: "https://passport.fe.test.sankuai.com/useraccount/ilogin",
      test: "https://passport.fe.test.sankuai.com/useraccount/ilogin",
      // development: "https://passport.fe.test.meituan.com/useraccount/ilogin",
      // test: "https://passport.fe.test.meituan.com/useraccount/ilogin",
      production: "https://passport.meituan.com/useraccount/ilogin",
    },
    "https://passport.meituan.com/useraccount/ilogin",
  );

  console.log("backUrl", backUrl);
  // 风控参数，下列参数也需要拼接到登陆url中
  // risk_partner 983
  // risk_app 216
  // risk_platform  3

  // 构建回调URL
  const finalBackUrl = backUrl || window.location.href;
  const url = new URL(loginUrl);
  if (finalBackUrl) {
    url.searchParams.set("backurl", finalBackUrl);
  }

  // 拼接风控参数
  url.searchParams.set("risk_partner", "983");
  url.searchParams.set("risk_app", "216");
  url.searchParams.set("risk_platform", "3");
  url.searchParams.set("allowTicketExchangeForToken", "true");
  // 跳转到登录页
  window.location.href = url.toString();
}

/**
 * 登录方法
 *
 * 根据环境自动选择登录方式：
 * - H5环境：跳转到i版登录页
 * - 美团APP环境：调用 KNB.login() 桥方法
 * - 美团小程序环境：使用 wx.miniProgram.redirectTo 跳转容器页面，设置 f_token=1 唤起登录
 *
 * 环境判断逻辑：
 * - 美团小程序：getAppEnv().isWXApp && getAppEnv().isMTWXApp（美团微信小程序H5容器）
 * - 美团APP内嵌H5：getAppEnv().isMT（使用KNB桥）
 * - 其余：getAppEnv().isBrowser 或其他非美团小程序、非美团APP场景，均按H5处理
 *
 * SSR兼容性：服务端渲染时跳过登录逻辑，返回rejected promise
 *
 * @param {LoginOptions} options 登录选项
 * @returns {Promise<void>} 登录结果
 */
export async function login(options: LoginOptions = { isLoginExpired: false }): Promise<void> {
  // SSR兼容性：服务端渲染时跳过登录逻辑
  if (isServer) {
    console.warn("[login] Login is not supported on server side");
    return Promise.reject(new Error("Login not available on server"));
  }

  const runtimeBackUrl =
    options.backUrl || (typeof window !== "undefined" ? window.location.href : "");

  try {
    // 美团小程序环境：使用 redirectTo 跳转容器页面，设置 f_token=1&f_userId=1 唤起登录
    // 机制：通过 f_xxx 参数控制容器行为，容器内部会调用小程序登录API
    if (getAppEnv().isWXApp && getAppEnv().isMTWXApp) {
      try {
        invokeMiniProgramLogin();
      } catch (error: any) {
        console.error("invokeMiniProgramLogin fail", error);
      }
      return;
    }

    // 美团APP环境：调用 KNB.login() 桥方法
    if (getAppEnv().isMT) {
      loginInMtApp(options);
      return;
    }

    // H5环境或其他环境：跳转到i版登录页URL，拼接 backUrl 参数
    // 包括：标准浏览器环境、其他APP环境（如点评APP）等
    // 文档来源：I版登录页文档（https://km.sankuai.com/collabpage/2726928774）- "如何登录"章节
    console.log("runtimeBackUrl", runtimeBackUrl);
    redirectToILogin(runtimeBackUrl);
  } catch (error: any) {
    console.error("login fail", error);
    if (typeof options?.onLoginFail === "function") {
      options.onLoginFail(error.message);
    }
  }
}

/**
 * 从参数中获取登陆信息,目前只在美小环境使用
 * 因为app环境当前登陆的实现，即存在token也无法判断登陆态是否正确
 */
export function getUserInfoFromUrl(): {
  userId: number | null;
  token: string;
  isLogin: boolean;
  isLoginExpired?: boolean;
  openId?: string;
  openIdCipher?: string;
  isInited?: boolean;
} {
  // 从参数中取 userId、token、uuid、
  // 如果是美小环境，还需要取 openId、openIdCipher
  // isLogin 能取到token则设置为true，否则设置为false，同时其他参数设置为 ''
  const urlParams = getUrlParams();
  const token = urlParams.token || "";
  const hasToken = !!token;
  // 如果能取到token，则返回实际参数值；否则设置为空字符串
  if (hasToken) {
    // 美团小程序环境：需要额外返回 openId 和 openIdCipher
    if (getAppEnv().isWXApp && getAppEnv().isMTWXApp) {
      const userInfo = {
        userId: urlParams.userId ? Number(urlParams.userId) : null,
        token: token,
        isLogin: true,
        isLoginExpired: false,
        isInited: true,
        openId: urlParams.openId || "",
        openIdCipher: urlParams.openIdCipher || "",
      };
      console.log("mtMiniprogram login info", userInfo);
      return userInfo;
    } else {
      return {
        userId: urlParams.userId ? Number(urlParams.userId) : null,
        token: token,
        isLogin: true,
        isInited: false, // 这个信息表示是否已经请求过接口，其他环境需要等待接口返回才会设置为true。美小则直接从参数判断
      };
    }
  } else {
    // 没有token时，其他参数设置为空字符串
    // 如果是美小环境，取完参数就把 isInited 设置为 true，即使没有 token 也是
    if (getAppEnv().isWXApp && getAppEnv().isMTWXApp) {
      return {
        userId: null,
        token: "",
        isLogin: false,
        isLoginExpired: false,
        isInited: true, // 美小环境直接从参数判断，取完参数就设置为 true
        openId: urlParams.openId || "",
        openIdCipher: urlParams.openIdCipher || "",
      };
    } else {
      return {
        userId: null,
        token: "",
        isLogin: false,
        isLoginExpired: false,
        isInited: false, // 这个信息表示是否已经请求过接口，其他环境需要等待接口返回才会设置为true。美小则直接从参数判断
      };
    }
  }
}

/**
 * 客户端获取用户信息（通用方法）
 *
 * 前端共同约定的结论：
 * - 美小环境直接取参数设置userInfo
 * - 其他环境等接口返回设置userInfo
 * - 接口请求同步，只是渲染的时候等这个接口返回。（并行请求）
 *
 * @returns {Promise<UserInfo | null>} 用户信息，失败时返回null
 */
export async function fetchUserInfoClient(): Promise<UserInfo | null> {
  let userInfo: UserInfo | null = null;
  try {
    // 只有美小环境走参数，其他环境等接口。
    // 因为其他环境不知道登陆态是否正确 （h5、外卖容器）。
    if (getAppEnv().isWXApp && getAppEnv().isMTWXApp) {
      console.log("appEnv isMTWXApp");
      userInfo = getUserInfoFromUrl();
      // 美小环境直接从 URL 参数获取用户信息
      // 如果用户没登录，参数token为空，上报 tokenEmpty
      if (userInfo.isLogin) {
        reportGetUserInfoSuccessRate(true, "success");
      } else {
        reportGetUserInfoSuccessRate(true, "tokenEmpty");
      }
      cachedUserInfo = userInfo;
      return userInfo;
    } else {
      const res = await getUserInfo();
      console.log("getUserInfo res", res);

      if (res.code === 200) {
        userInfo = res.data;
        if (userInfo) {
          userInfo.isInited = true;
          userInfo.isLogin = true;
          userInfo.isLoginExpired = false;
        }
        reportGetUserInfoSuccessRate(true, "success");
        cachedUserInfo = userInfo;
        return userInfo;
      } else if (res.code === 5002) {
        reportGetUserInfoSuccessRate(true, "tokenEmpty");
        cachedUserInfo = {
          userId: null,
          token: "",
          isLogin: false,
          isLoginExpired: false,
          isInited: true,
        };
        return cachedUserInfo;
      }
    }
    // 其他环境统一走下接口，app环境也需要走下接口，因为app环境无法判断登陆态过期情况
    console.log("getUserInfo res", userInfo);
    cachedUserInfo = userInfo;
    return userInfo;
  } catch (error) {
    // 请求库的统一处理逻辑：
    // 1. 业务状态码错误（code !== 0 && code !== 200）：Promise.reject(response.data)
    //    此时 error 是 { code, message, data } 结构，需要检查 error.code
    // 2. 网络错误（HTTP 状态码错误）：Promise.reject(error)
    //    此时 error 是 axios error 对象，需要检查 error.response?.status

    const businessCode = error && typeof error === "object" ? (error as any)?.code : undefined;

    // 处理业务状态码  5002（token为空）
    if (businessCode === 5002) {
      reportGetUserInfoSuccessRate(true, "tokenEmpty");
      userInfo = {
        userId: null,
        token: "",
        isLogin: false,
        isLoginExpired: false,
        isInited: true,
      };
      cachedUserInfo = userInfo;
      console.log("getUserInfo res return 5002/5002 (未登录)", userInfo);
      return userInfo;
    }

    // 再检查 HTTP 状态码（网络错误时，error 是 axios error 对象）
    const httpStatus =
      error && typeof error === "object"
        ? ((error as any)?.response?.status ?? (error as any)?.status)
        : undefined;

    // 处理业务状态码 401/403/404/405（登录过期）
    // 1. HTTP 状态码为 401/403/404/405
    // 2. HTTP 状态码为 200，但业务状态码（code）为 401/403/404/405
    const isHttpStatusError =
      httpStatus === 401 || httpStatus === 403 || httpStatus === 404 || httpStatus === 405;
    const isBusinessCodeError =
      businessCode === 401 || businessCode === 403 || businessCode === 404 || businessCode === 405;
    const isLoginExpiredError = isHttpStatusError || isBusinessCodeError;

    if (isLoginExpiredError) {
      reportGetUserInfoSuccessRate(true, "tokenExpired");
      userInfo = {
        userId: null,
        token: "",
        isLogin: false,
        isLoginExpired: true, // 处理HTTP状态码错误或业务状态码错误，主要是在app环境需要先调用退登
        isInited: true,
      };
      cachedUserInfo = userInfo;
      console.log("getUserInfo res return 401/403/404/405 (登录过期)", userInfo);
      return userInfo;
    }

    // 未处理的其他错误
    reportGetUserInfoSuccessRate(false, "getFail");
    console.error("获取用户信息失败", error);
    cachedUserInfo = null;
    return null;
  }
}

export function getCacheUserInfo(): UserInfo | null {
  return cachedUserInfo;
}
